// src/services/api/exportService.ts
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  ReporteVentasDetalle,
  TopProducto,
  ProductoRentabilidad,
  ReporteComprasPorProveedor,
  ReporteImpuestos,
  ReporteInventario,
  ReporteDevoluciones,
  ReporteClientes,
  ReporteVendedores,
  AnalisisABC,
} from '../../components/types/reportes.types';

export class ExportService {
  /**
   * Convertir string/number a number de forma segura
   */
  private static toNumber(value: string | number | undefined | null): number {
    if (value === undefined || value === null) return 0;
    return typeof value === 'string' ? Number(value) || 0 : value;
  }

  /**
   * Exportar ventas a Excel
   */
  static exportarVentasExcel(
    ventas: ReporteVentasDetalle[],
    nombreArchivo: string = 'reporte-ventas'
  ): void {
    const datosExcel = ventas.map((venta) => ({
      'Código Factura': venta.CodigoFacturaVenta,
      'Fecha': new Date(venta.FechaVenta).toLocaleDateString('es-CO'),
      'Cliente': venta.Cliente,
      'Vendedor': venta.Usuario,
      'Subtotal': venta.Subtotal,
      'Impuesto': venta.Impuesto,
      'Total': venta.Total,
      'Estado': venta.EstadoVenta,
      'Origen': venta.Origen,
      'Período': venta.Periodo,
    }));

    this.generarExcel(datosExcel, nombreArchivo, 'Reporte de Ventas');
  }

  /**
   * Exportar compras a Excel
   */
  static exportarComprasExcel(
    compras: ReporteComprasPorProveedor[],
    nombreArchivo: string = 'reporte-compras'
  ): void {
    const datosExcel = compras.map((compra) => ({
      'Proveedor': compra.Proveedor,
      'Total Compras': compra.TotalCompras,
      'Monto Total': compra.MontoTotal,
      'Compra Promedio': compra.CompraPromedio,
      'Tiempo Entrega Promedio (días)': compra.TiempoEntregaPromedio,
      'Devoluciones': compra.Devoluciones,
    }));

    this.generarExcel(datosExcel, nombreArchivo, 'Reporte de Compras');
  }

  /**
   * Exportar top productos a Excel
   */
  static exportarTopProductosExcel(
    productos: TopProducto[],
    nombreArchivo: string = 'top-productos'
  ): void {
    const datosExcel = productos.map((producto, index) => ({
      'Posición': index + 1,
      'Producto': producto.Producto,
      'EAN': producto.EAN,
      'Categoría': producto.Categoria,
      'Cantidad Vendida': producto.CantidadVendida,
      'Monto Total': producto.MontoTotal,
      'Número de Ventas': producto.NumeroVentas,
      'Venta Promedio': parseFloat((producto.MontoTotal / producto.NumeroVentas).toFixed(2)),
    }));

    this.generarExcel(datosExcel, nombreArchivo, 'Top Productos');
  }

  /**
   * Exportar rentabilidad a Excel
   */
  static exportarRentabilidadExcel(
    productos: ProductoRentabilidad[],
    nombreArchivo: string = 'rentabilidad-productos'
  ): void {
    const datosExcel = productos.map((producto) => ({
      'Producto': producto.Producto,
      'Cantidad Vendida': producto.CantidadVendida,
      'Precio Compra Promedio': producto.PrecioCompraPromedio,
      'Precio Venta Promedio': producto.PrecioVentaPromedio,
      'Ingreso Total': producto.IngresoTotal,
      'Costo Total': producto.CostoTotal,
      'Utilidad Bruta': producto.UtilidadBruta,
      'Margen (%)': producto.MargenPorcentaje,
    }));

    this.generarExcel(datosExcel, nombreArchivo, 'Rentabilidad');
  }

  /**
   * Exportar informe de impuestos a Excel
   */
  static exportarImpuestosExcel(
    impuestos: ReporteImpuestos[],
    nombreArchivo: string = 'informe-impuestos'
  ): void {
    const datosExcel = impuestos.map((registro) => ({
      'Período': registro.Periodo,
      'Total Ventas': this.toNumber(registro.TotalVentas),
      'Total Devoluciones': this.toNumber(registro.TotalDevoluciones),
      'Base Gravable Bruta': registro.BaseGravableBruta || 0,
      'Base Gravable Neta': registro.BaseGravableNeta || 0,
      'IVA Bruto': registro.ImpuestoBruto || 0,
      'IVA Neto': registro.ImpuestoNeto || 0,
      'IVA Devuelto': registro.ImpuestoDevuelto || 0,
      'Ventas Netas': registro.VentasNetas || 0,
    }));

    // Agregar fila de totales
    const totales = {
      'Período': 'TOTAL',
      'Total Ventas': impuestos.reduce((sum, i) => sum + this.toNumber(i.TotalVentas), 0),
      'Total Devoluciones': impuestos.reduce((sum, i) => sum + this.toNumber(i.TotalDevoluciones), 0),
      'Base Gravable Bruta': impuestos.reduce((sum, i) => sum + (i.BaseGravableBruta || 0), 0),
      'Base Gravable Neta': impuestos.reduce((sum, i) => sum + (i.BaseGravableNeta || 0), 0),
      'IVA Bruto': impuestos.reduce((sum, i) => sum + (i.ImpuestoBruto || 0), 0),
      'IVA Neto': impuestos.reduce((sum, i) => sum + (i.ImpuestoNeto || 0), 0),
      'IVA Devuelto': impuestos.reduce((sum, i) => sum + (i.ImpuestoDevuelto || 0), 0),
      'Ventas Netas': impuestos.reduce((sum, i) => sum + (i.VentasNetas || 0), 0),
    };

    datosExcel.push(totales);

    this.generarExcel(datosExcel, nombreArchivo, 'Informe de Impuestos');
  }

  /**
   * Exportar inventario a Excel
   */
  static exportarInventarioExcel(
    inventario: ReporteInventario[],
    nombreArchivo: string = 'reporte-inventario'
  ): void {
    const datosExcel = inventario.map((item) => ({
      'EAN': item.EAN,
      'Producto': item.Producto,
      'Categoría': item.Categoria,
      'Stock Actual': item.StockActual,
      'Stock Mínimo': item.StockMinimo,
      'Estado': item.StockActual <= item.StockMinimo ? 'BAJO' : 'NORMAL',
      'Precio Compra': item.PrecioCompra,
      'Precio Venta': item.PrecioVenta,
      'Valor Inventario': item.ValorInventario,
      'Sucursal': item.Sucursal,
      'Última Compra': item.UltimaCompra ? new Date(item.UltimaCompra).toLocaleDateString('es-CO') : 'N/A',
      'Última Venta': item.UltimaVenta ? new Date(item.UltimaVenta).toLocaleDateString('es-CO') : 'N/A',
    }));

    // Agregar totales
    const totalValor = inventario.reduce((sum, i) => sum + i.ValorInventario, 0);
    const productosStockBajo = inventario.filter(i => i.StockActual <= i.StockMinimo).length;

    datosExcel.push({
      'EAN': '',
      'Producto': 'TOTALES',
      'Categoría': '',
      'Stock Actual': 0,
      'Stock Mínimo': 0,
      'Estado': `${productosStockBajo} productos bajo stock`,
      'Precio Compra': 0,
      'Precio Venta': 0,
      'Valor Inventario': totalValor,
      'Sucursal': '',
      'Última Compra': '',
      'Última Venta': '',
    });

    this.generarExcel(datosExcel, nombreArchivo, 'Inventario');
  }

  /**
   * Exportar devoluciones a Excel
   */
  static exportarDevolucionesExcel(
    devoluciones: ReporteDevoluciones[],
    nombreArchivo: string = 'reporte-devoluciones',
    tipo: 'venta' | 'compra' = 'venta'
  ): void {
    const datosExcel = devoluciones.map((dev) => ({
      'Código': dev.Codigo,
      'Fecha': new Date(dev.FechaDevolucion).toLocaleDateString('es-CO'),
      'Cliente/Proveedor': tipo === 'venta' ? (dev.Cliente || '') : (dev.Proveedor || ''),
      'Artículo': dev.Articulo,
      'Cantidad': dev.Cantidad,
      'Motivo': dev.Motivo,
      'Monto': dev.Monto,
      'Estado': dev.Estado,
      'Observación': dev.Observacion || 'N/A',
    }));

    // Agregar totales
    const totalMonto = devoluciones.reduce((sum, d) => sum + d.Monto, 0);
    const totalCantidad = devoluciones.reduce((sum, d) => sum + d.Cantidad, 0);

    datosExcel.push({
      'Código': 'TOTAL',
      'Fecha': '',
      'Cliente/Proveedor': '',
      'Artículo': '',
      'Cantidad': totalCantidad,
      'Motivo': '',
      'Monto': totalMonto,
      'Estado': '',
      'Observación': '',
    });

    const titulo = tipo === 'venta' 
      ? 'Devoluciones de Venta' 
      : 'Devoluciones de Compra';
    
    this.generarExcel(datosExcel, nombreArchivo, titulo);
  }

  /**
   * Exportar clientes a Excel
   */
  static exportarClientesExcel(
    clientes: ReporteClientes[],
    nombreArchivo: string = 'reporte-clientes'
  ): void {
    const datosExcel = clientes.map((cliente) => {
      const diasUltimaCompra = cliente.UltimaCompra 
        ? Math.floor((new Date().getTime() - new Date(cliente.UltimaCompra).getTime()) / (1000 * 60 * 60 * 24))
        : 0;

      return {
        'Cliente': cliente.Cliente,
        'Tipo Documento': cliente.TipoDocumento,
        'Número Documento': cliente.NumeroDocumento,
        'Total Compras': cliente.TotalCompras,
        'Monto Total': cliente.MontoTotal,
        'Compra Promedio': cliente.CompraPromedio,
        'Última Compra': new Date(cliente.UltimaCompra).toLocaleDateString('es-CO'),
        'Días Última Compra': diasUltimaCompra,
        'Estado': cliente.Estado,
      };
    });

    this.generarExcel(datosExcel, nombreArchivo, 'Reporte de Clientes');
  }

  /**
   * Exportar vendedores a Excel
   */
  static exportarVendedoresExcel(
    vendedores: ReporteVendedores[],
    nombreArchivo: string = 'reporte-vendedores'
  ): void {
    const datosExcel = vendedores.map((vendedor) => ({
      'Vendedor': vendedor.Vendedor,
      'Total Ventas': vendedor.TotalVentas,
      'Monto Total': vendedor.MontoTotal,
      'Venta Promedio': vendedor.VentaPromedio,
      'Comisión': vendedor.Comision || 0,
      'Meta': vendedor.Meta || 0,
      'Cumplimiento (%)': vendedor.Cumplimiento || 0,
      'Clientes Atendidos': vendedor.ClientesAtendidos,
    }));

    const cumplimientoPromedio = vendedores.reduce((sum, v) => sum + (v.Cumplimiento || 0), 0) / vendedores.length;

    // Agregar totales
    const totales = {
      'Vendedor': 'TOTAL',
      'Total Ventas': vendedores.reduce((sum, v) => sum + v.TotalVentas, 0),
      'Monto Total': vendedores.reduce((sum, v) => sum + v.MontoTotal, 0),
      'Venta Promedio': vendedores.reduce((sum, v) => sum + v.VentaPromedio, 0) / vendedores.length,
      'Comisión': vendedores.reduce((sum, v) => sum + (v.Comision || 0), 0),
      'Meta': vendedores.reduce((sum, v) => sum + (v.Meta || 0), 0),
      'Cumplimiento (%)': parseFloat(cumplimientoPromedio.toFixed(2)),
      'Clientes Atendidos': vendedores.reduce((sum, v) => sum + v.ClientesAtendidos, 0),
    };

    datosExcel.push(totales);

    this.generarExcel(datosExcel, nombreArchivo, 'Performance Vendedores');
  }

  /**
   * Exportar análisis ABC a Excel
   */
  static exportarAnalisisABCExcel(
    analisis: AnalisisABC[],
    nombreArchivo: string = 'analisis-abc'
  ): void {
    const workbook = XLSX.utils.book_new();

    // Hoja 1: Resumen
    const resumen = analisis.map((clase) => ({
      'Clasificación': clase.Clasificacion,
      'Número de Productos': clase.NumeroProductos,
      '% de Productos': parseFloat(clase.PorcentajeProductos.toFixed(2)),
      'Monto Total': clase.MontoTotal,
      '% de Ventas': clase.PorcentajeVentas,
    }));

    const wsResumen = XLSX.utils.json_to_sheet(resumen);
    XLSX.utils.book_append_sheet(workbook, wsResumen, 'Resumen');

    // Hojas 2-4: Detalle por clasificación
    analisis.forEach((clase) => {
      if (clase.Productos && clase.Productos.length > 0) {
        const productos = clase.Productos.map((p, index) => ({
          'Posición': index + 1,
          'Producto': p.Nombre,
          'Monto Ventas': p.MontoVentas,
          '% Acumulado': parseFloat(p.PorcentajeAcumulado.toFixed(2)),
        }));

        const ws = XLSX.utils.json_to_sheet(productos);
        XLSX.utils.book_append_sheet(workbook, ws, `Clase ${clase.Clasificacion}`);
      }
    });

    // Exportar
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const timestamp = new Date().toISOString().split('T')[0];
    saveAs(blob, `${nombreArchivo}_${timestamp}.xlsx`);
  }

  /**
   * Exportar reporte consolidado (múltiples hojas)
   */
  static exportarReporteConsolidado(
    datos: {
      ventas?: ReporteVentasDetalle[];
      compras?: ReporteComprasPorProveedor[];
      productos?: TopProducto[];
      rentabilidad?: ProductoRentabilidad[];
      impuestos?: ReporteImpuestos[];
      inventario?: ReporteInventario[];
    },
    nombreArchivo: string = 'reporte-consolidado'
  ): void {
    const workbook = XLSX.utils.book_new();

    // Hoja de Resumen Ejecutivo
    const resumenEjecutivo = [
      { Métrica: 'Total Ventas', Valor: datos.ventas?.length || 0 },
      { Métrica: 'Monto Total Ventas', Valor: datos.ventas?.reduce((sum, v) => sum + v.Total, 0) || 0 },
      { Métrica: 'Total Compras', Valor: datos.compras?.reduce((sum, c) => sum + c.TotalCompras, 0) || 0 },
      { Métrica: 'Monto Total Compras', Valor: datos.compras?.reduce((sum, c) => sum + c.MontoTotal, 0) || 0 },
      { Métrica: 'Productos en Inventario', Valor: datos.inventario?.length || 0 },
      { Métrica: 'Valor Total Inventario', Valor: datos.inventario?.reduce((sum, i) => sum + i.ValorInventario, 0) || 0 },
      { 
        Métrica: 'Total Impuestos Neto', 
        Valor: datos.impuestos?.reduce((sum, i) => sum + (i.ImpuestoNeto || 0), 0) || 0 
      },
      { 
        Métrica: 'Total Impuestos Bruto', 
        Valor: datos.impuestos?.reduce((sum, i) => sum + (i.ImpuestoBruto || 0), 0) || 0 
      },
    ];
    const wsResumen = XLSX.utils.json_to_sheet(resumenEjecutivo);
    XLSX.utils.book_append_sheet(workbook, wsResumen, 'Resumen Ejecutivo');

    // Hoja de Ventas
    if (datos.ventas && datos.ventas.length > 0) {
      const ventasData = datos.ventas.map((v) => ({
        'Factura': v.CodigoFacturaVenta,
        'Fecha': new Date(v.FechaVenta).toLocaleDateString('es-CO'),
        'Cliente': v.Cliente,
        'Total': v.Total,
        'Estado': v.EstadoVenta,
      }));
      const wsVentas = XLSX.utils.json_to_sheet(ventasData);
      XLSX.utils.book_append_sheet(workbook, wsVentas, 'Ventas');
    }

    // Hoja de Compras
    if (datos.compras && datos.compras.length > 0) {
      const comprasData = datos.compras.map((c) => ({
        'Proveedor': c.Proveedor,
        'Total Compras': c.TotalCompras,
        'Monto': c.MontoTotal,
      }));
      const wsCompras = XLSX.utils.json_to_sheet(comprasData);
      XLSX.utils.book_append_sheet(workbook, wsCompras, 'Compras');
    }

    // Hoja de Top Productos
    if (datos.productos && datos.productos.length > 0) {
      const productosData = datos.productos.map((p, i) => ({
        'Pos': i + 1,
        'Producto': p.Producto,
        'Vendidas': p.CantidadVendida,
        'Monto': p.MontoTotal,
      }));
      const wsProductos = XLSX.utils.json_to_sheet(productosData);
      XLSX.utils.book_append_sheet(workbook, wsProductos, 'Top Productos');
    }

    // Hoja de Rentabilidad
    if (datos.rentabilidad && datos.rentabilidad.length > 0) {
      const rentData = datos.rentabilidad.map((r) => ({
        'Producto': r.Producto,
        'Vendidas': r.CantidadVendida,
        'Ingresos': r.IngresoTotal,
        'Costos': r.CostoTotal,
        'Utilidad': r.UtilidadBruta,
        'Margen %': r.MargenPorcentaje,
      }));
      const wsRent = XLSX.utils.json_to_sheet(rentData);
      XLSX.utils.book_append_sheet(workbook, wsRent, 'Rentabilidad');
    }

    // Hoja de Impuestos
    if (datos.impuestos && datos.impuestos.length > 0) {
      const impData = datos.impuestos.map((i) => ({
        'Período': i.Periodo,
        'Ventas': this.toNumber(i.TotalVentas),
        'Devoluciones': this.toNumber(i.TotalDevoluciones),
        'Base Gravable Neta': i.BaseGravableNeta || 0,
        'IVA Neto': i.ImpuestoNeto || 0,
        'Ventas Netas': i.VentasNetas || 0,
      }));
      const wsImp = XLSX.utils.json_to_sheet(impData);
      XLSX.utils.book_append_sheet(workbook, wsImp, 'Impuestos');
    }

    // Hoja de Inventario
    if (datos.inventario && datos.inventario.length > 0) {
      const invData = datos.inventario.map((i) => ({
        'Producto': i.Producto,
        'EAN': i.EAN,
        'Stock': i.StockActual,
        'Mínimo': i.StockMinimo,
        'Valor': i.ValorInventario,
        'Estado': i.StockActual <= i.StockMinimo ? 'BAJO' : 'NORMAL',
      }));
      const wsInv = XLSX.utils.json_to_sheet(invData);
      XLSX.utils.book_append_sheet(workbook, wsInv, 'Inventario');
    }

    // Exportar
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const timestamp = new Date().toISOString().split('T')[0];
    saveAs(blob, `${nombreArchivo}_${timestamp}.xlsx`);
  }

  /**
   * Método genérico para generar Excel
   */
  private static generarExcel(
    datos: any[],
    nombreArchivo: string,
    nombreHoja: string
  ): void {
    const worksheet = XLSX.utils.json_to_sheet(datos);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, nombreHoja);

    // Ajustar ancho de columnas automáticamente
    const maxWidth = 30;
    const colWidths: { wch: number }[] = [];
    
    // Calcular ancho basado en el contenido
    if (datos.length > 0) {
      const headers = Object.keys(datos[0]);
      headers.forEach((header) => {
        let maxLen = header.length;
        datos.forEach((row) => {
          const cellValue = String(row[header] || '');
          maxLen = Math.max(maxLen, cellValue.length);
        });
        colWidths.push({ wch: Math.min(maxLen + 2, maxWidth) });
      });
      worksheet['!cols'] = colWidths;
    }

    // Exportar
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const timestamp = new Date().toISOString().split('T')[0];
    saveAs(blob, `${nombreArchivo}_${timestamp}.xlsx`);
  }

  /**
   * Exportar a CSV
   */
  static exportarCSV(datos: any[], nombreArchivo: string): void {
    const worksheet = XLSX.utils.json_to_sheet(datos);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const timestamp = new Date().toISOString().split('T')[0];
    saveAs(blob, `${nombreArchivo}_${timestamp}.csv`);
  }

  /**
   * Exportar a PDF (requiere jsPDF y autoTable)
   */
  static async exportarPDF(
    datos: any[],
    nombreArchivo: string,
    titulo: string
  ): Promise<void> {
    console.warn('La exportación a PDF estará disponible próximamente');   
  }
}